import { DialogContent, DialogTitle, Grid, Stack } from '@mui/material';
import { FormProvider, useForm } from 'react-hook-form';
import { createNewBusinessUnitSettings } from 'wisestep-api';
import { ControlledTextInput, WsButton, WsTypography, WsTypoVariants } from 'wisestep-base';

import { useWorkspaceContext } from '../../../context';
import { AtsCustomAPIAccess, useAtsCustomAPIAccess } from '../../../hooks/useAtsCustomAPIAccess';
import { failed, success, warning } from '../../../utils/NotificationMessages';
import { useNotificationStore } from '../../../utils/NotificationStore';
import { clientSettingsDefaultPayload } from '../helper/ClientSettingsHelper';

interface BusinessUnit {
  name: string;
  id?: string;
}

export interface FormProps<T> {
  onClose(): void;
  onSave: (data: T) => void;
  defaultValues?: T;
}

const BusinessUnitForm = ({ onClose, onSave, defaultValues }: FormProps<BusinessUnit>) => {
  const form = useForm({ defaultValues });
  const setNotification = useNotificationStore((state) => state.setNotification);

  const workspaceContext = useWorkspaceContext();
  const { access: atsCustomAccessControl } = useAtsCustomAPIAccess([
    AtsCustomAPIAccess.MANAGE_CLIENT_SETTINGS,
  ]);

  const manageAccess = workspaceContext.owns || atsCustomAccessControl[0];

  const onSubmit = async (data) => {
    if (!manageAccess) {
      setNotification(warning('You do not have enough permissions to perform this action'));
      return;
    }
    const payload = { ...clientSettingsDefaultPayload };
    payload.name = data.name;
    try {
      if (payload.name) {
        const { data: savedBusinessUnit } = await createNewBusinessUnitSettings(payload);
        setNotification(success(`${data.name} has been added successfully !!`));
        onSave(savedBusinessUnit);
      } else setNotification(failed('Please input the name again !!'));
    } catch (error: any) {
      if (error.response.status === 400)
        setNotification(warning(`Oops , ${data.name} already exists!!`));
      else setNotification(failed('Something went wrong !!'));
    }
  };

  return (
    <>
      <DialogTitle>
        <WsTypography variant={WsTypoVariants.SUB_TITLE}>Business Unit</WsTypography>
      </DialogTitle>
      <DialogContent>
        <FormProvider {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)}>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <ControlledTextInput
                  name="name"
                  size="medium"
                  placeholder="Enter New Business Unit"
                  required
                  fullWidth
                />
              </Grid>
              <Grid item xs={12}>
                <Stack direction="row" justifyContent="flex-end" spacing={2}>
                  <WsButton onClick={onClose} variant="outlined" size="large">
                    Close
                  </WsButton>
                  <WsButton type="submit" size="large">
                    Save
                  </WsButton>
                </Stack>
              </Grid>
            </Grid>
          </form>
        </FormProvider>
      </DialogContent>
    </>
  );
};

export default BusinessUnitForm;
